import { useEffect, useState } from 'react'
import { PlusIcon } from '@heroicons/react/20/solid'
import { AdManagementService, type MetaConnectionRead } from '../../client'
import { apiErrorMessage } from '../../lib/api'
import { openMetaAuthPopup } from '../../lib/metaAuth'
import Modal from '../ui/Modal'
import MetaIcon from './MetaIcon'

type Props = {
  brandId: number
  onClose: () => void
  onSwitched: () => void
}

export default function ConnectionSwitcher({ brandId, onClose, onSwitched }: Props) {
  const [connections, setConnections] = useState<MetaConnectionRead[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    AdManagementService.getConnections(brandId)
      .then(setConnections)
      .catch(() => setError('Failed to load connections. Please try again.'))
      .finally(() => setLoading(false))
  }, [brandId])

  async function switchTo(connectionId: number) {
    setBusy(true)
    setError('')

    try {
      await AdManagementService.switchConnection({ brand_id: brandId, connection_id: connectionId })
      onSwitched()
    } catch {
      setError('Failed to switch connection.')
    } finally {
      setBusy(false)
    }
  }

  async function connectNew() {
    setBusy(true)
    setError('')

    try {
      await openMetaAuthPopup(brandId)
      onSwitched()
    } catch (err) {
      setError(err instanceof Error ? err.message : apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="Meta Connections" onClose={onClose}>
      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((row) => (
            <div key={row} className="h-14 animate-pulse rounded-lg bg-gray-200" />
          ))}
        </div>
      ) : error && connections.length === 0 ? (
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">{error}</p>
      ) : (
        <div className="space-y-2">
          {connections.map((connection) => {
            const active = connection.status === 'active'

            return (
              <div
                key={connection.id}
                onClick={() => !active && !busy && void switchTo(connection.id)}
                className={`flex items-center justify-between rounded-lg border p-3 transition ${
                  active ? 'border-indigo-300 bg-indigo-50' : 'cursor-pointer border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <MetaIcon className="size-5 text-[#1877F2]" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {connection.meta_user_name || 'Meta User'}
                    </p>
                    {connection.last_used_ad_account_name && (
                      <p className="truncate text-xs text-gray-500">{connection.last_used_ad_account_name}</p>
                    )}
                  </div>
                </div>
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    active ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {active ? 'Active' : 'Inactive'}
                </span>
              </div>
            )
          })}

          <button
            type="button"
            disabled={busy}
            onClick={() => void connectNew()}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 p-3 text-sm text-gray-600 transition hover:border-gray-400 hover:text-gray-900 disabled:opacity-60"
          >
            <PlusIcon className="size-4" />
            Connect new account
          </button>

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      )}
    </Modal>
  )
}
